import { palmettoFinanceOrgAlias } from '@/components/auth/RequirePalmettoFinanceUser';
import usePermissions from '@/hooks/usePermissions';
import {
  useGetOrganizationReviewSlaSettingsQuery,
  useUpdateOrganizationReviewSlaSettingsMutation,
} from '@/services/organizations';
import { OrganizationNode } from '@/types/Organizations';
import {
  Box,
  Button,
  Card,
  FormikSelectInputNative,
  FormikTextInput,
  Icon,
  Spinner,
  toast,
  Toggle,
} from '@palmetto/palmetto-components';
import { Field, Formik, FormikHelpers, useFormikContext } from 'formik';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import {
  MilestoneLabels,
  MilestoneType,
  OrgV2ReviewSlaMilestoneHours,
  OrgV2ReviewSlaSettings,
  ProgramType,
  ProgramTypeLabels,
  UserPermissions,
} from 'types';

const InheritedSettingsToggle = ({
  disabled,
  organizationName,
  sourceName,
  forceCustom,
}: {
  disabled: boolean;
  organizationName?: string;
  sourceName?: string;
  forceCustom?: boolean;
}) => {
  const { values, setFieldValue } = useFormikContext<QCPriorityFormValues>();
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const custom = event.target.checked; // toggle on = custom settings
      void setFieldValue('inherit', !custom);
      if (!custom) {
        void setFieldValue('enabled', false);
      }
    },
    [setFieldValue],
  );
  return (
    <Box
      direction="row"
      borderColor="separator"
      borderWidth="sm"
      radius="md"
      background={values.inherit ? undefined : 'warning-50'}
      justifyContent="space-between"
      margin="lg 0"
    >
      <Box direction={{ base: 'column', tablet: 'row' }} margin="0 sm sm sm">
        <Box direction="column" childGap="xs">
          {values.inherit ? (
            <>
              <Box fontWeight="bold" margin="sm 0">
                Using Inherited Settings
              </Box>
              <Box>Settings are inherited from {sourceName ?? '-'}. Turn this on to configure custom settings.</Box>
            </>
          ) : (
            <>
              <Box fontWeight="bold" margin="sm 0">
                Using Custom Settings
              </Box>
              <Box>
                {forceCustom
                  ? 'Custom settings are activated.'
                  : `Custom settings are activate for ${organizationName ?? '-'}. These will override anything inherited from ${sourceName ?? '-'}.`}
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Box justifyContent="center" margin="sm">
        <Toggle
          id="inheritedSettings"
          label="Use custom settings"
          hideLabel
          isChecked={!values.inherit}
          isDisabled={disabled || forceCustom}
          onChange={handleChange}
        />
      </Box>
    </Box>
  );
};

const QcPriorityEnabledToggle = ({ disabled }: { disabled: boolean }) => {
  const { values, setFieldValue } = useFormikContext<QCPriorityFormValues>();
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => void setFieldValue('enabled', event.target.checked),
    [setFieldValue],
  );
  return (
    <Toggle
      id="qcPriorityEnabled"
      label="QC Priority"
      hideLabel
      isChecked={values.enabled}
      isDisabled={disabled}
      onChange={handleChange}
    />
  );
};

// The QC review package milestones that carry a submission/resubmission SLA budget.
const REVIEW_SLA_MILESTONES = [MilestoneType.install, MilestoneType.activation];

const REVIEW_PRIORITY_TIER_OPTIONS = [
  { label: 'Select a tier', value: '' },
  { label: 'Tier 1', value: '1' },
  { label: 'Tier 2', value: '2' },
  { label: 'Tier 3', value: '3' },
];

type MilestoneHoursFormValue = {
  submission: string;
  resubmission: string;
};

interface QCPriorityFormValues {
  enabled: boolean;
  inherit: boolean;
  programType: string;
  reviewPriorityTier: string;
  milestones: Record<string, MilestoneHoursFormValue>;
}

const buildDefaultMilestones = (
  slaBusinessHoursByMilestone?: Partial<Record<MilestoneType, OrgV2ReviewSlaMilestoneHours>>,
): Record<string, MilestoneHoursFormValue> =>
  REVIEW_SLA_MILESTONES.reduce<Record<string, MilestoneHoursFormValue>>((acc, milestone) => {
    const hours = slaBusinessHoursByMilestone?.[milestone];
    acc[milestone] = {
      submission: hours?.submission != null ? String(hours.submission) : '',
      resubmission: hours?.resubmission != null ? String(hours.resubmission) : '',
    };
    return acc;
  }, {});

const toOptionalNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : Number(trimmed);
};

const requiredWhenCustom = (message: string) => ({
  is: true,
  then: (schema: yup.AnySchema) => schema.notRequired(),
  otherwise: (schema: yup.AnySchema) => schema.required(message),
});

const hoursField = yup
  .number()
  .transform((value, original) => (String(original).trim() === '' ? undefined : value))
  .typeError('Enter a number of hours')
  .min(0, 'Must be 0 or greater')
  .notRequired();

const validationSchema = yup.object().shape({
  enabled: yup.boolean(),
  inherit: yup.boolean(),
  programType: yup.string().when('inherit', requiredWhenCustom('Program type is required')),
  reviewPriorityTier: yup.string().when('inherit', requiredWhenCustom('Review priority tier is required')),
  milestones: yup.object().shape(
    REVIEW_SLA_MILESTONES.reduce<Record<string, yup.AnyObjectSchema>>((acc, milestone) => {
      acc[milestone] = yup.object().shape({ submission: hoursField, resubmission: hoursField });
      return acc;
    }, {}),
  ),
});

type MilestoneHoursRowProps = {
  milestone: MilestoneType;
  disabled: boolean;
};

const MilestoneHoursRow = ({ milestone, disabled }: MilestoneHoursRowProps) => (
  <Box direction="row" height="lg">
    <Box width={{ base: '100', tablet: '4xl' }} height="lg" borderWidth="sm sm 0 0" borderColor="separator">
      <Box margin="md" fontWeight="bold">
        {MilestoneLabels[milestone]}
      </Box>
    </Box>
    <Box width={{ base: '100', tablet: '4xl' }} height="lg" borderWidth="sm sm 0 0" borderColor="separator">
      <Field
        margin="xs"
        type="text"
        inputMode="decimal"
        label={`${MilestoneLabels[milestone]} submission SLA`}
        hideLabel
        name={`milestones.${milestone}.submission`}
        id={`milestones.${milestone}.submission`}
        component={FormikTextInput}
        suffix="hrs"
        isDisabled={disabled}
        width="3xl"
      />
    </Box>
    <Box width={{ base: '100', tablet: '4xl' }} height="lg" borderWidth="sm 0 0 0" borderColor="separator">
      <Field
        margin="xs"
        type="text"
        inputMode="decimal"
        label={`${MilestoneLabels[milestone]} resubmission SLA`}
        hideLabel
        name={`milestones.${milestone}.resubmission`}
        id={`milestones.${milestone}.resubmission`}
        component={FormikTextInput}
        suffix="hrs"
        isDisabled={disabled}
        width="3xl"
      />
    </Box>
  </Box>
);

const formatHoursOrDash = (value?: number | null): string => (value != null ? `${value} hrs` : '-');
const formatTierOrDash = (value?: number | null): string => (value != null ? `Tier ${value}` : '-');

const InheritedValue = ({ label, value }: { label: string; value: string }) => (
  <Box direction="column" width={{ base: '100', tablet: '4xl' }} margin="0 0 0 lg">
    <Box fontWeight="bold" color="body-tertiary" margin="sm 0">
      {label}
    </Box>
    <Box>{value}</Box>
  </Box>
);

const InheritedSettingsPanel = ({
  value,
  sourceName,
  showTier,
}: {
  value?: OrgV2ReviewSlaSettings | null;
  sourceName?: string;
  showTier: boolean;
}) => {
  const install = value?.slaBusinessHoursByMilestone?.[MilestoneType.install];
  const activation = value?.slaBusinessHoursByMilestone?.[MilestoneType.activation];
  const qcPriority = value?.enabled == null ? '-' : value.enabled ? 'Enabled' : 'Disabled';

  return (
    <Box background="success" radius="md">
      <Box direction="column" margin="sm" childGap="xs">
        <Box direction="row" fontWeight="bold" margin="sm 0" childGap="xs">
          <Icon name="link" /> Inherited from {sourceName ?? '-'}
        </Box>
        <Box>
          <Box direction="row" justifyContent="space-between">
            {showTier && (
              <InheritedValue label="PRIORITY TIER" value={formatTierOrDash(value?.reviewPriorityTier)} />
            )}
            <InheritedValue label="INSTALL - INITIAL SLA" value={formatHoursOrDash(install?.submission)} />
            <InheritedValue label="INSTALL - RESUBMISSION SLA" value={formatHoursOrDash(install?.resubmission)} />
          </Box>
          <Box direction="row" justifyContent="space-between">
            <InheritedValue label="QC PRIORITY" value={qcPriority} />
            <InheritedValue label="ACTIVATION - INITIAL SLA" value={formatHoursOrDash(activation?.submission)} />
            <InheritedValue
              label="ACTIVATION - RESUBMISSION SLA"
              value={formatHoursOrDash(activation?.resubmission)}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const ProgramTypeSync = ({ onProgramTypeChange }: { onProgramTypeChange: (programType: ProgramType) => void }) => {
  const { values } = useFormikContext<QCPriorityFormValues>();
  useEffect(() => {
    if (values.programType) {
      onProgramTypeChange(values.programType as ProgramType);
    }
  }, [values.programType, onProgramTypeChange]);
  return null;
};

export type QCPriorityProps = {
  organization: OrganizationNode;
};

export const QCPriority = ({ organization }: QCPriorityProps) => {
  const alias = organization.alias;
  const permissions = usePermissions();
  const navigate = useNavigate();

  const canManage = permissions.includes(UserPermissions.lightReachQCSuperAdmin);

  const [programType, setProgramType] = useState<ProgramType>(
    (organization.programTypes?.[0] as ProgramType) ?? ProgramType.solar,
  );

  const { data, isLoading } = useGetOrganizationReviewSlaSettingsQuery({ alias, programType });
  const setting = data?.setting ?? null;
  const isInherited = data?.isInherited ?? false;

  const sourceName = data?.source?.name;
  const organizationName = organization?.name;
  const inheritedSetting = data?.inheritedSetting ?? null;
  // source is absent from the API response for root parents (no parent to inherit from)
  const isRootParent = !data?.source;

  const [updateReviewSlaSettings, { isLoading: isSubmitting }] = useUpdateOrganizationReviewSlaSettingsMutation();
  const programTypeOptions = useMemo(
    () =>
      (organization.programTypes ?? []).map((option) => ({
        label: ProgramTypeLabels[option] ?? option,
        value: option,
      })),
    [organization.programTypes],
  );

  const initialValues = useMemo<QCPriorityFormValues>(
    () => ({
      enabled: setting?.value?.enabled ?? false,
      // Root parents can't inherit, so they're always custom; otherwise inherit when:
      // - the effective setting was inherited from a parent (isInherited), OR
      // - there's no setting anywhere (setting == null), OR
      // - the org's own setting doc explicitly says inherit: true (setting.inherit), meaning
      //   the org chose to inherit even though the parent currently has no setting to provide.
      inherit: isRootParent ? false : isInherited || setting == null || (setting?.inherit ?? false),
      programType,
      reviewPriorityTier: setting?.value?.reviewPriorityTier != null ? String(setting.value.reviewPriorityTier) : '',
      milestones: buildDefaultMilestones(setting?.value?.slaBusinessHoursByMilestone),
    }),
    [setting, isInherited, isRootParent, programType],
  );

  const onSubmit = useCallback(
    async (data: QCPriorityFormValues, { setSubmitting }: FormikHelpers<QCPriorityFormValues>) => {
      const slaBusinessHoursByMilestone = REVIEW_SLA_MILESTONES.reduce<
        Partial<Record<MilestoneType, OrgV2ReviewSlaMilestoneHours>>
      >((acc, milestone) => {
        const submission = toOptionalNumber(data.milestones[milestone].submission);
        const resubmission = toOptionalNumber(data.milestones[milestone].resubmission);
        if (submission != null || resubmission != null) {
          acc[milestone] = { submission, resubmission };
        }
        return acc;
      }, {});

      try {
        await updateReviewSlaSettings({
          alias,
          inherit: data.inherit,
          programType: data.programType ? (data.programType as ProgramType) : undefined,
          enabled: data.enabled,
          reviewPriorityTier: toOptionalNumber(data.reviewPriorityTier),
          slaBusinessHoursByMilestone,
        }).unwrap();
        toast.success('QC priority settings saved');
      } catch {
        toast.error('Failed to save QC priority settings');
      } finally {
        setSubmitting(false);
      }
    },
    [alias, updateReviewSlaSettings],
  );

  const handleCancel = useCallback(
    () => navigate(`/settings/organizations/${organization.alias}`),
    [navigate, organization.alias],
  );

  if (alias === palmettoFinanceOrgAlias) {
    return <Box padding="lg">QC Priority configuration is not available at the Palmetto Finance level.</Box>;
  }

  if (isLoading) {
    return (
      <Box padding="lg" alignItems="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <Box padding="lg" childGap="lg">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnChange={false}
        enableReinitialize
        onSubmit={onSubmit}
      >
        {({ values, submitForm }) => (
          <>
            <ProgramTypeSync onProgramTypeChange={setProgramType} />
            <Card>
              <Card.Section
                title={
                  <Box direction="column" childGap="xs">
                    <Box fontWeight="bold">QC Review Priority</Box>
                    <Box>Controls queue prioritization and review time targets for this org's packages.</Box>
                  </Box>
                }
              >
                <Box childGap="lg" margin="md 0">
                  <Field
                    label="Program Type"
                    name="programType"
                    id="programType"
                    options={programTypeOptions}
                    component={FormikSelectInputNative}
                    isDisabled={!canManage}
                    autoComplete="off"
                    width="3xl"
                    style={{ marginRight: 'var(--size-spacing-xl)' }}
                  />
                  <InheritedSettingsToggle
                    disabled={!canManage}
                    sourceName={sourceName}
                    forceCustom={isRootParent}
                    organizationName={organizationName}
                  />
                  <Box
                    direction="row"
                    borderColor="separator"
                    borderWidth="sm"
                    radius="md"
                    justifyContent="space-between"
                    background="secondary"
                  >
                    <Box direction={{ base: 'column', tablet: 'row' }} margin="0 sm sm sm">
                      <Box direction="column" childGap="xs">
                        <Box fontWeight="bold" margin="sm 0">
                          QC Priority {values.enabled ? 'Enabled' : 'Disabled'}
                        </Box>
                        <Box>This org uses custom QC prioritization and SLA targets.</Box>
                      </Box>
                    </Box>
                    <Box justifyContent="center" margin="sm">
                      <QcPriorityEnabledToggle disabled={!canManage || values.inherit} />
                    </Box>
                  </Box>

                  {canManage && !values.inherit && values.enabled && (
                    <Box>
                      <Box direction="row" gap="xs" alignItems="center">
                        <Box fontWeight="bold" margin="sm 0">
                          Review Priority Tier
                        </Box>
                        <Box margin="sm 0" color="body-tertiary">
                          • required
                        </Box>
                      </Box>
                      <Box direction="row" height="xl" background="success" radius="md">
                        <Box direction="column" margin="sm">
                          <Field
                            label="Review Priority Tier"
                            hideLabel
                            name="reviewPriorityTier"
                            id="reviewPriorityTier"
                            options={REVIEW_PRIORITY_TIER_OPTIONS}
                            component={FormikSelectInputNative}
                            isRequired
                            isDisabled={!canManage}
                            autoComplete="off"
                            width="3xl"
                            style={{ marginRight: 'var(--size-spacing-xl)' }}
                          />
                          <Box margin="sm 0">Applies to all packages types</Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                  {values.inherit && (
                    <InheritedSettingsPanel
                      value={inheritedSetting?.value}
                      sourceName={sourceName}
                      showTier={canManage}
                    />
                  )}
                </Box>
              </Card.Section>

              {!values.inherit && values.enabled && (
                <Card.Section title="SLA Targets by Package Type">
                  <Box>
                    <Box direction="row" background="success" radius="md md 0 0">
                      <Box
                        direction="row"
                        width={{ base: '100', tablet: '4xl' }}
                        borderWidth="0 sm 0 0"
                        borderColor="separator"
                      >
                        <Box margin="md" fontWeight="bold">
                          Package Type
                        </Box>
                      </Box>
                      <Box
                        direction="row"
                        width={{ base: '100', tablet: '4xl' }}
                        borderWidth="0 sm 0 0"
                        borderColor="separator"
                      >
                        <Box margin="md" direction="row">
                          <Box fontWeight="bold">Initial Submission SLA</Box>
                          <Box margin="0 0 0 sm" color="body-tertiary">
                            • optional
                          </Box>
                        </Box>
                      </Box>
                      <Box width={{ base: '100', tablet: '4xl' }} borderWidth="0 0 0 0" borderColor="separator">
                        <Box margin="md" direction="row">
                          <Box fontWeight="bold">Resubmission SLA</Box>
                          <Box margin="0 0 0 sm" color="body-tertiary">
                            • optional
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    {REVIEW_SLA_MILESTONES.map((milestone) => (
                      <MilestoneHoursRow key={milestone} milestone={milestone} disabled={!canManage} />
                    ))}
                  </Box>
                </Card.Section>
              )}

              {canManage && (
                <Card.Section>
                  <Box
                    direction={{
                      base: 'column',
                      tablet: 'row',
                    }}
                    alignItems={{
                      base: 'stretch',
                      tablet: 'flex-end',
                    }}
                    justifyContent={{
                      tablet: 'flex-end',
                    }}
                    childGap="sm"
                    style={{ flexShrink: 0 }}
                  >
                    <Button
                      as="a"
                      navigate={handleCancel}
                      variant="secondary"
                      tone="neutral"
                      size="md"
                      isDisabled={isLoading || isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      size="md"
                      type="button"
                      onClick={submitForm}
                      isDisabled={isLoading || isSubmitting}
                      isLoading={isSubmitting}
                    >
                      Save
                    </Button>
                  </Box>
                </Card.Section>
              )}
            </Card>
          </>
        )}
      </Formik>
    </Box>
  );
};
